import {
  StackView,
  Box,
  Input,
  Checkbox,
  Text,
  Tooltip,
  Link
} from "@planningcenter/tapestry-react"
import PropTypes from "prop-types"
import { formatDateShort, formatMonthDayCondensed } from "utils/dates"
import moment from "moment-timezone"
import { Modal } from "../modals/Modal"

const SelectionTypesOptionsModal = ({
  maxAttendees,
  setMaxAttendees,
  isSimple,
  signupTimes,
  setSignupTimes,
  resetSignupTimes,
  enableSplitRegistrations,
  canChangeRegisterForSeparateTimes,
  setEnableSplitRegistrations,
  handleSaveClick,
  handleCancel
}) => {
  const formatDateTime = dateTime => {
    const startsAt = moment(dateTime.startsAt)
    const endsAt = moment(dateTime.endsAt)
    const allDay = dateTime.allDay
    const { twentyFourHourTime } = App?.currentOrganization || {}

    return (
      <>
        {startsAt.isSame(endsAt, "day") ? (
          <>{startsAt.format("MMM D, YYYY")}</>
        ) : (
          <>
            {startsAt.isSame(endsAt, "year") ? (
              <>
                {startsAt.isSame(endsAt, "month") ? (
                  <>
                    {formatMonthDayCondensed(startsAt)}&ndash;
                    {endsAt.format("D, YYYY")}
                  </>
                ) : (
                  <>
                    {formatMonthDayCondensed(startsAt)} &ndash; {formatDateShort(endsAt)}
                  </>
                )}
              </>
            ) : (
              <>
                {formatDateShort(startsAt)} &ndash; {formatDateShort(endsAt)}
              </>
            )}
          </>
        )}

        {!allDay && (
          <>
            {", "}
            <span>
              {twentyFourHourTime ? (
                <>
                  {startsAt.format("HH:mm")}
                  &ndash;
                  {endsAt.format("HH:mm")}
                </>
              ) : (
                <>
                  {startsAt.minute() === 0 ? startsAt.format("h") : startsAt.format("h:mm")}
                  {startsAt.format("a") !== endsAt.format("a") && startsAt.format("a")}
                  &ndash;
                  {endsAt.minute() === 0 ? endsAt.format("h") : endsAt.format("h:mm")}
                  {endsAt.format("a")}
                </>
              )}
            </span>
          </>
        )}
      </>
    )
  }

  const handleEnableSplitRegistrationsClick = () => {
    const newState = !enableSplitRegistrations
    setEnableSplitRegistrations(newState)
    if (!newState) {
      resetSignupTimes()
    }
  }

  const updateEventTime = eventTime => {
    setSignupTimes(
      signupTimes.map(st => {
        if (st.id !== eventTime.id) {
          return st
        } else {
          return {
            ...st,
            availableForRegistrations: !eventTime.availableForRegistrations
          }
        }
      })
    )
  }

  const renderSplitTimes = () => {
    if (enableSplitRegistrations && signupTimes && signupTimes.length) {
      return (
        <>
          <Text weight={700} size="13px">
            Dates/times available for registration
          </Text>
          {signupTimes.map(time => (
            <Checkbox
              key={time.id}
              size="md"
              label={formatDateTime(time)}
              checked={time.availableForRegistrations}
              onChange={() => updateEventTime(time)}
            />
          ))}
        </>
      )
    }

    if (!signupTimes || !signupTimes.length) {
      return (
        <Text className="c-neutral-600" size="14px">
          To use split registrations, you need to add dates/times to your signup.{" "}
          <SplitRegistrationsLearnMoreLink />
        </Text>
      )
    }

    if (!enableSplitRegistrations && !canChangeRegisterForSeparateTimes) {
      return (
        <Text className="c-neutral-600" size="14px">
          Split registrations cannot be enabled after people have registered.{" "}
          <SplitRegistrationsLearnMoreLink />
        </Text>
      )
    }

    return (
      <Text className="c-neutral-600" size="14px">
        With split registrations, you can collect separate registrations for each date/time in your
        event. <SplitRegistrationsLearnMoreLink />
      </Text>
    )
  }

  return (
    <Modal title="Options" onModalSubmit={handleSaveClick} onModalCancel={handleCancel}>
      <Box marginBottom={2}>
        <h4 className="label">Maximum selections per registration</h4>
        <Input
          type="number"
          className="tapestry-input"
          value={maxAttendees}
          onChange={e => setMaxAttendees(e.target.value)}
          placeholder="Unlimited"
          min={1}
        />
      </Box>
      {isSimple && (
        <StackView spacing={1}>
          <Tooltip
            placement="bottom"
            title={
              enableSplitRegistrations && !canChangeRegisterForSeparateTimes
                ? "Split registrations cannot be disabled after people have registered"
                : ""
            }
          >
            <Checkbox
              size="lg"
              label="Enable split registrations"
              checked={enableSplitRegistrations}
              onChange={handleEnableSplitRegistrationsClick}
              disabled={!canChangeRegisterForSeparateTimes || !signupTimes?.length}
            />
          </Tooltip>
          <StackView marginLeft={3} spacing={0.5}>
            {renderSplitTimes()}
          </StackView>
        </StackView>
      )}
    </Modal>
  )
}

SelectionTypesOptionsModal.propTypes = {
  modalOpen: PropTypes.bool,
  setModalOpen: PropTypes.func,
  maxAttendees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setMaxAttendees: PropTypes.func,
  isSimple: PropTypes.bool,
  signupTimes: PropTypes.array,
  setSignupTimes: PropTypes.func,
  resetSignupTimes: PropTypes.func,
  enableSplitRegistrations: PropTypes.bool,
  canChangeRegisterForSeparateTimes: PropTypes.bool,
  setEnableSplitRegistrations: PropTypes.func,
  handleSaveClick: PropTypes.func,
  handleCancel: PropTypes.func,
  status: PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string
  })
}

export default SelectionTypesOptionsModal

const SplitRegistrationsLearnMoreLink = () => {
  return (
    <Link to="https://pcoregistrations.zendesk.com/hc/en-us/articles/1260804541869" external>
      Learn more
    </Link>
  )
}
